import { MenuItem } from "@chakra-ui/react";
import { feeInformationToCreation } from "features/fees/FeeUtils";
import { ReadOnlyFeeForm } from "features/fees/form/ReadOnlyFeeForm";
import { useCommitmentInformationRetrieveSuspense } from "gen/endpoints/commitment-information/commitment-information";
import { useFeeInformationByFeeListSuspense } from "gen/endpoints/fee-information/fee-information";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

// disabled for now https://maybern.atlassian.net/browse/ENG-6828
const DISABLE_EDITING = true;

export const CommitmentFeePage: React.FC = () => {
  const { t } = useTranslation(["fees", "common", "enums"]);
  const navigate = useNavigate();

  const { fundFamilyId, commitmentId, feeId } = useRequiredStringParams();

  const { data: feeData } = useFeeInformationByFeeListSuspense(
    fundFamilyId,
    feeId,
    {
      security_ownership_id: commitmentId,
    },
    { query: { select: feeInformationToCreation } },
  );
  const { data: commitmentData } = useCommitmentInformationRetrieveSuspense(
    fundFamilyId,
    commitmentId,
  );

  return (
    <PageContent contentWidth="boxed">
      <PageHeader
        title={feeData.fee.name}
        backButton={{
          title: t("common:fees"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .commitments({})
            .commitment({ commitmentId })
            .fees({}).$,
        }}
        metadata={[
          {
            title: commitmentData.owning_entity_legal_name,
            to: rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .setup({})
              .investorEntities({})
              .investorEntity({
                investorEntityId: commitmentData.owning_entity_id,
              })
              .tabActivity({}).$,
          },
          {
            title: t("common:commitment"),
            to: rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .commitments({})
              .commitment({ commitmentId }).$,
          },
          {
            title: t("common:fee"),
          },
        ]}
        actions={
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          DISABLE_EDITING ? undefined : (
            <Restricted permission={PermissionTypeEnum.config}>
              <PageMenu>
                <MenuItem
                  onClick={() =>
                    navigate(
                      rootRoute({})
                        .fundFamilies({})
                        .fundFamily({ fundFamilyId })
                        .commitments({})
                        .feeOverride({ commitmentId, feeId }).$,
                    )
                  }
                >
                  {t("common:edit")}
                </MenuItem>
              </PageMenu>
            </Restricted>
          )
        }
      />
      <PageBody>
        <ReadOnlyFeeForm fundFamilyId={fundFamilyId} feeData={feeData} />
      </PageBody>
    </PageContent>
  );
};
